import type { ReduxSagaModel, InvokeResult } from '@/models/types';
import GenerativeDashboard from '@/services/generativeDashboard';
import { handleUserError } from '@/utils/handleError';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { assertIsDefined, assertIsString } from '@/utils/typeChecks';
import { logger } from '@/utils/logger';
import { generateTitle } from '@/woLint/models/generativeDashboard';

// Cap the prompt copy PERSISTED on the dashboard row. The full prompt is
// still passed to the title and to the view's chat so the brief
// phase sees everything the user typed — including a pasted meeting
// transcript. But the STORED `prompt` attribute only ever feeds a
// CSS-truncated list card + a 240-char setup-summary, and the lambda never
// reads it back; a 39KB transcript on the row needlessly eats the DDB
// 400KB item budget (it helped push dashboard 20a433e7 over the cap).
// NOTE: if a future "regenerate from stored prompt" feature is added, it
// must re-fetch the full text from elsewhere — the stored copy is lossy.
const STORED_PROMPT_MAX_CHARS = 4096;
const capStoredPrompt = (p: string): string =>
  (p || '').length > STORED_PROMPT_MAX_CHARS ? (p || '').slice(0, STORED_PROMPT_MAX_CHARS) : (p || '');

const ns = 'app.models.generativeDashboard';

export interface State {
  current: GenerativeDashboard | null;
  list: InvokeResult<GenerativeDashboard[]> | null;
  invokeCreateResult: InvokeResult<string> | null;
  invokeDeleteResult: InvokeResult<null> | null;
  generatedTitle: string | null;
  // Anonymized previews for the Home picker, keyed by dashboard id, as presigned URLs. `null` means
  // fetched and there is none (not rendered yet, or unreachable); a missing key means not fetched.
  thumbnails: Record<string, string | null>;
  // When each entry of `thumbnails` was fetched. The URLs are signed for 900s, so an entry older
  // than THUMBNAIL_URL_TTL_MS is fetched again rather than served expired.
  thumbnailsFetchedAt: Record<string, number>;
}

const THUMBNAIL_URL_TTL_MS = 10 * 60 * 1000;

const initialState: State = {
  current: null,
  list: null,
  invokeCreateResult: null,
  invokeDeleteResult: null,
  generatedTitle: null,
  thumbnails: {},
  thumbnailsFetchedAt: {},
};

// Discriminated-union action types for each reducer. Mirrors the
// pattern in src/models/dashboard.ts so the ReduxSagaModel<State, TReducers>
// generic can infer state/payload types throughout the file.
export type SaveCurrentAction = {
  type: 'saveCurrent' | 'generativeDashboard/saveCurrent';
  payload: Pick<State, 'current'>;
};
type SaveListAction = {
  type: 'saveList';
  payload: Pick<State, 'list'>;
};
type SaveInvokeCreateResultAction = {
  type: 'saveInvokeCreateResult';
  payload: Pick<State, 'invokeCreateResult'>;
};
type SaveInvokeDeleteResultAction = {
  type: 'saveInvokeDeleteResult';
  payload: Pick<State, 'invokeDeleteResult'>;
};
type SaveGeneratedTitleAction = {
  type: 'saveGeneratedTitle';
  payload: Pick<State, 'generatedTitle'>;
};
type SaveThumbnailAction = {
  type: 'saveThumbnail';
  payload: { id: string; url: string | null; fetchedAt: number };
};

export type GetThumbnailsEffect = {
  type: 'generativeDashboard/getThumbnails';
  payload: { ids: string[] };
};

const GenerativeDashboardModel: ReduxSagaModel<
  State,
  {
    saveCurrent: SaveCurrentAction;
    saveList: SaveListAction;
    saveInvokeCreateResult: SaveInvokeCreateResultAction;
    saveInvokeDeleteResult: SaveInvokeDeleteResultAction;
    saveGeneratedTitle: SaveGeneratedTitleAction;
    saveThumbnail: SaveThumbnailAction;
  }
> = {
  namespace: 'generativeDashboard',
  state: initialState,

  effects: {
    *get({ payload }: { payload: { id: string } }, { put }) {
      try {
        const dashboard = yield* sgcall(() => GenerativeDashboard.getById(payload.id));
        // Guard against stale responses — only update if this dashboard is still current
        const currentState = yield* sgselect((s) => s.generativeDashboard.current);
        if (currentState && currentState.id !== payload.id) return;
        yield put({ type: 'saveCurrent', payload: { current: dashboard } });
      } catch (e) {
        handleUserError(e, `${ns}.get.error`);
        yield put({ type: 'saveCurrent', payload: { current: null } });
      }
    },

    // Fetched one by one and saved as each lands, so the picker's cards fill in progressively
    // rather than waiting on the slowest. Ids fetched within the URL's lifetime are skipped.
    *getThumbnails({ payload }: { payload: GetThumbnailsEffect['payload'] }, { put }) {
      const list = yield* sgselect((s) => s.generativeDashboard.list);
      const fetchedAt = yield* sgselect((s) => s.generativeDashboard.thumbnailsFetchedAt);
      if (!list?.success) return;
      const now = Date.now();
      const wanted = list.data.filter(
        (d) =>
          payload.ids.includes(d.id) &&
          !(d.id in fetchedAt && now - fetchedAt[d.id] < THUMBNAIL_URL_TTL_MS),
      );
      for (const dashboard of wanted) {
        const url = yield* sgcall(() => GenerativeDashboard.getThumbnailUrl(dashboard));
        yield put<SaveThumbnailAction>({
          type: 'saveThumbnail',
          payload: { id: dashboard.id, url, fetchedAt: Date.now() },
        });
      }
    },

    *getList(_, { put }) {
      try {
        const workspaceId = yield* sgselect((s) => s.organization.currentWorkspace);
        assertIsString(workspaceId, 'BEEM260422100001');
        const dashboards = yield* sgcall(() =>
          GenerativeDashboard.getByWorkspace(workspaceId),
        );
        yield put({
          type: 'saveList',
          payload: { list: { success: true, data: dashboards } },
        });
      } catch (e) {
        yield put({
          type: 'saveList',
          payload: { list: { success: false, error: handleUserError(e, `${ns}.getList.error`) } },
        });
      }
    },

    *invokeCreate({ payload }: { payload: { name: string; prompt: string } }, { put }) {
      try {
        const workspaceId = yield* sgselect((s) => s.organization.currentWorkspace);
        assertIsString(workspaceId, 'BEEM260422100002');
        const currentUser = yield* sgselect((s) => s.user.currentUser); assertIsDefined(currentUser, 'BEEM260424100100');
        const dashboardId = yield* sgcall(() =>
          GenerativeDashboard.create({
            name: payload.name,
            workspaceId,
            // Store a capped copy only — the row never needs the full text.
            prompt: capStoredPrompt(payload.prompt),
            status: 'GENERATING',
            createdBy: currentUser.id,
          }),
        );
        yield put({
          type: 'saveInvokeCreateResult',
          payload: { invokeCreateResult: { success: true, data: dashboardId } },
        });
      } catch (e) {
        yield put({
          type: 'saveInvokeCreateResult',
          payload: {
            invokeCreateResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeCreate.error`),
            },
          },
        });
      }
    },

    *invokeDelete({ payload }: { payload: { id: string } }, { put }) {
      try {
        yield* sgcall(() => GenerativeDashboard.delete(payload.id));
        yield put({
          type: 'saveInvokeDeleteResult',
          payload: { invokeDeleteResult: { success: true, data: null } },
        });
        // A deleted Home dashboard would leave the workspace pointing at nothing. Home already
        // treats a missing target as "not set", but clearing the pointer keeps Settings honest.
        const workspace = yield* sgselect((s) => s.workspaces.current);
        if (workspace?.homeDashboardId === payload.id) {
          yield put({ type: 'workspaces/invokeSetHomeDashboard', payload: { dashboardId: null } });
        }
        yield put({ type: 'getList', payload: {} });
      } catch (e) {
        yield put({
          type: 'saveInvokeDeleteResult',
          payload: {
            invokeDeleteResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeDelete.error`),
            },
          },
        });
      }
    },

    *invokeGenerate(
      {
        payload,
      }: {
        payload: {
          prompt: string;
          // Optional client-generated UUID. When the caller has already
          // uploaded attachments under this id's S3 prefix
          // (CreateModal pre-uploads to bypass the chicken-and-egg
          // between "need a dashboardId for the upload key" and "need
          // the upload to finish before creating the dashboard"), they
          // pass it here so the freshly-created dashboard row uses the
          // SAME id. Lambda finds the attachments by the same prefix.
          // Omitted callers fall back to server-generated UUID.
          id?: string;
        };
      },
      { put },
    ) {
      try {
        const workspaceId = yield* sgselect((s) => s.organization.currentWorkspace);
        assertIsString(workspaceId, 'BEEM260422200001');
        const currentUser = yield* sgselect((s) => s.user.currentUser); assertIsDefined(currentUser, 'BEEM260424100100');

        // 1. Create the dashboard record in GENERATING state. When the
        // caller supplied a client-generated id (brief-path multi-modal
        // flow — attachments were pre-uploaded under that id's S3
        // prefix), pass it through so the lambda finds them under the
        // same key. Without an explicit id the GraphQL resolver
        // generates one server-side as before.
        const dashboardId = yield* sgcall(() =>
          GenerativeDashboard.create({
            ...(payload.id ? { id: payload.id } : {}),
            name: 'Generating...',
            workspaceId,
            // Store a capped copy only; the full prompt goes to the view's chat, which asks it.
            prompt: capStoredPrompt(payload.prompt),
            status: 'GENERATING',
            createdBy: currentUser.id,
          }),
        );

        yield put({
          type: 'saveInvokeCreateResult',
          payload: { invokeCreateResult: { success: true, data: dashboardId } },
        });


        // Title generation runs alongside (fast, lightweight LLM call). The dashboard itself is
        // built by the view's chat, which asks the prompt, with its attachments, as the first
        // turn on the agent's dashboard profile (BEEM-1448).
        yield put({
          type: 'invokeGenerateTitle',
          payload: { dashboardId, prompt: payload.prompt },
        });

      } catch (e) {
        logger.debug({ label: `${ns}.invokeGenerate.error`, message: e });
        const errorMessage = e instanceof Error ? e.message : 'Generation failed';
        // Update orphaned dashboard to ERROR state
        const createRes = yield* sgselect((s) => s.generativeDashboard.invokeCreateResult);
        if (createRes?.success && createRes.data) {
          try {
            yield* sgcall(() =>
              GenerativeDashboard.update({
                id: createRes.data,
                status: 'ERROR',
                errorMessage,
              }),
            );
          } catch (updateErr) {
            // Best-effort: the user-visible failure is already reported via
            // the saga's `saveInvokeCreateResult` below. Log so the silent
            // status update failure is at least visible in dev tools.
            logger.debug({ label: `${ns}.invokeGenerate.errorUpdate`, message: updateErr });
          }
        }
        yield put({
          type: 'saveInvokeCreateResult',
          payload: {
            invokeCreateResult: {
              success: false,
              error: handleUserError(e, `${ns}.invokeGenerate.error`),
            },
          },
        });
      }
    },

    *invokeGenerateTitle(
      { payload }: { payload: { dashboardId: string; prompt: string } },
      { put },
    ) {
      try {
        yield put({ type: 'saveGeneratedTitle', payload: { generatedTitle: null } });
        const title = yield* sgcall(() => generateTitle(payload.prompt));
        yield put({ type: 'saveGeneratedTitle', payload: { generatedTitle: title } });
        // Also persist the title to the dashboard record
        yield* sgcall(() =>
          GenerativeDashboard.update({ id: payload.dashboardId, name: title }),
        );
      } catch (e) {
        logger.debug({ label: `${ns}.invokeGenerateTitle.error`, message: e });
      }
    },

  },

  reducers: {
    resetAll() {
      return { ...initialState };
    },
    saveCurrent(state, { payload }) {
      return { ...state, ...payload };
    },
    saveList(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeCreateResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveInvokeDeleteResult(state, { payload }) {
      return { ...state, ...payload };
    },
    saveGeneratedTitle(state, { payload }) {
      return { ...state, ...payload };
    },
    saveThumbnail(state, { payload }) {
      return {
        ...state,
        thumbnails: { ...state.thumbnails, [payload.id]: payload.url },
        thumbnailsFetchedAt: { ...state.thumbnailsFetchedAt, [payload.id]: payload.fetchedAt },
      };
    },
  },
};

export default GenerativeDashboardModel;
